import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import { validatePassword } from '../utils/passwordValidation';

/**
 * Dynamic Password Strength Indicator.
 * @param {string} password - Current password input value.
 * @param {object} policy - Policy configuration from backend.
 *
 * Strength rules:
 *  - "Strong" (green) ONLY when ALL required policy rules are met AND the
 *    password does NOT contain character types that are not required by policy.
 *  - If the user types characters from non-required categories → capped at "Good" (medium).
 *  - Partial rule satisfaction → Weak / Fair / Good based on progress.
 */
const PasswordStrengthIndicator = ({ password, policy }) => {
  const status = validatePassword(password, policy);

  if (!password) return null;

  if (status.strength === 'Loading...') {
    return (
      <div className="mt-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700">
        <span className="text-xs text-gray-400">Loading password policy...</span>
      </div>
    );
  }

  if (status.requirements.length === 0) return null;

  // Explicit static color map — all class names must be static strings for Tailwind JIT.
  const colorMap = {
    'Very Weak': { bar: 'bg-red-500',    label: 'text-red-500'    },
    'Weak':      { bar: 'bg-orange-500', label: 'text-orange-500' },
    'Fair':      { bar: 'bg-yellow-500', label: 'text-yellow-600' },
    'Good':      { bar: 'bg-blue-500',   label: 'text-blue-600'   },
    'Strong':    { bar: 'bg-green-500',  label: 'text-green-600'  },
  };

  // Strength is calculated entirely from the validation utility, based only on matched requirements.
  const displayStrength = status.strength;

  const colors = colorMap[displayStrength] || { bar: 'bg-gray-200', label: 'text-gray-400' };

  // Bar width reflects the actual raw score from the validation utility.
  const strengthWidth = {
    'Very Weak': 15,
    'Weak':      35,
    'Fair':      55,
    'Good':      75,
    'Strong':   100,
  };
  const barWidth = strengthWidth[displayStrength] ?? status.score;

  return (
    <div className="mt-3 space-y-3 animate-fade-in p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700">
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Password Strength</span>
        <span className={`text-xs font-bold ${colors.label}`}>
          {displayStrength}
        </span>
      </div>

      {/* Strength Bar */}
      <div className="h-1.5 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full transition-all duration-500 ease-out ${colors.bar}`}
          style={{ width: `${barWidth}%` }}
        />
      </div>

      {/* Checklist */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
        {status.requirements.map((item, index) => (
          <div key={index} className="flex items-center gap-2 transition-all duration-300">
            {item.met ? (
              <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
            ) : (
              <div className="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600 shrink-0" />
            )}
            <span className={`text-xs font-medium transition-colors ${item.met ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
              {item.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PasswordStrengthIndicator;
